/**
 * Service pour la gestion des permissions et rôles.
 */
import api from './api';
import { 
  Permission, 
  Role, 
  RolePermission, 
  UserRole, 
  PermissionLog, 
  PermissionCode
} from '@/types/permissions';

class PermissionService {
  // ============================================================================
  // PERMISSIONS
  // ============================================================================
  
  /**
   * Récupère toutes les permissions
   */
  async getPermissions(): Promise<Permission[]> {
    const response = await api.get('/permissions/permissions/');
    return response.data.results || response.data;
  }

  /**
   * Récupère les permissions groupées par catégorie
   */
  async getPermissionsByCategory(): Promise<Record<string, Permission[]>> {
    const response = await api.get('/permissions/permissions/by_category/');
    return response.data;
  }

  /**
   * Récupère les permissions de l'utilisateur connecté
   */
  async getCurrentUserPermissions(): Promise<Permission[]> {
    const response = await api.get('/permissions/permissions/user_permissions/');
    return response.data;
  }

  // ============================================================================
  // RÔLES
  // ============================================================================
  
  /**
   * Récupère tous les rôles
   */
  async getRoles(): Promise<Role[]> {
    const response = await api.get('/permissions/roles/');
    return response.data.results || response.data;
  }

  /**
   * Récupère un rôle par ID
   */
  async getRole(id: number): Promise<Role> {
    const response = await api.get(`/permissions/roles/${id}/`);
    return response.data;
  }

  /**
   * Crée un nouveau rôle
   */
  async createRole(roleData: Partial<Role>): Promise<Role> {
    const response = await api.post('/permissions/roles/', roleData);
    return response.data;
  }

  /**
   * Met à jour un rôle
   */
  async updateRole(id: number, roleData: Partial<Role>): Promise<Role> {
    const response = await api.put(`/permissions/roles/${id}/`, roleData);
    return response.data;
  }

  /**
   * Supprime un rôle
   */
  async deleteRole(id: number): Promise<void> {
    await api.delete(`/permissions/roles/${id}/`);
  }

  /**
   * Récupère les permissions d'un rôle
   */
  async getRolePermissions(roleId: number): Promise<RolePermission[]> {
    const response = await api.get(`/permissions/roles/${roleId}/permissions/`);
    return response.data;
  }

  /**
   * Assigne une permission à un rôle
   */
  async assignPermissionToRole(roleId: number, permissionId: number, accessLevel: string = 'READ'): Promise<RolePermission> {
    const response = await api.post(`/permissions/roles/${roleId}/assign_permission/`, {
      permission_id: permissionId,
      access_level: accessLevel
    });
    return response.data;
  }

  /**
   * Retire une permission d'un rôle
   */
  async removePermissionFromRole(roleId: number, permissionId: number): Promise<void> {
    await api.delete(`/permissions/roles/${roleId}/remove_permission/`, {
      data: { permission_id: permissionId }
    });
  }

  // ============================================================================
  // RÔLES UTILISATEURS
  // ============================================================================
  
  /**
   * Récupère tous les rôles utilisateurs
   */
  async getUserRoles(userId?: number): Promise<UserRole[]> {
    const url = userId 
      ? `/permissions/user-roles/?user_id=${userId}`
      : '/permissions/user-roles/';
    const response = await api.get(url);
    return response.data.results || response.data;
  }

  /**
   * Récupère les rôles de l'utilisateur connecté
   */
  async getMyRoles(): Promise<Role[]> {
    const response = await api.get('/permissions/user-roles/user_roles/');
    return response.data;
  }

  /**
   * Assigne un rôle à un utilisateur
   */
  async assignRoleToUser(userId: number, roleId: number, expiresAt?: string): Promise<UserRole> {
    const response = await api.post('/permissions/user-roles/assign_role/', {
      user_id: userId,
      role_id: roleId,
      expires_at: expiresAt
    });
    return response.data;
  }

  /**
   * Retire un rôle d'un utilisateur
   */
  async removeRoleFromUser(userRoleId: number): Promise<void> {
    await api.delete(`/permissions/user-roles/${userRoleId}/remove_role/`);
  }

  // ============================================================================
  // PERMISSIONS UTILISATEURS
  // ============================================================================
  
  /**
   * Récupère les permissions d'un utilisateur
   */
  async getUserPermissions(userId: number): Promise<Permission[]> {
    const response = await api.get(`/permissions/user-permissions/${userId}/permissions/`);
    return response.data;
  }

  /**
   * Récupère les rôles d'un utilisateur
   */
  async getUserRolesForUser(userId: number): Promise<Role[]> {
    const response = await api.get(`/permissions/user-permissions/${userId}/roles/`);
    return response.data;
  }

  /**
   * Vérifie si l'utilisateur connecté a une permission
   */
  async checkPermission(permissionCode: PermissionCode): Promise<boolean> {
    const response = await api.get(`/permissions/user-permissions/check_permission/?permission=${permissionCode}`);
    return response.data.has_permission;
  }

  /**
   * Récupère les permissions de l'utilisateur connecté
   */
  async getMyPermissions(): Promise<{ permissions: Permission[]; permission_codes: string[] }> {
    const response = await api.get('/permissions/user-permissions/my_permissions/');
    return response.data;
  }

  // ============================================================================
  // LOGS DE PERMISSIONS
  // ============================================================================
  
  /**
   * Récupère les logs de permissions
   */
  async getPermissionLogs(userId?: number, action?: string): Promise<PermissionLog[]> {
    let url = '/permissions/permission-logs/';
    const params = new URLSearchParams();
    
    if (userId) params.append('user_id', userId.toString());
    if (action) params.append('action', action);
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    const response = await api.get(url);
    return response.data.results || response.data;
  }

  // ============================================================================
  // MÉTHODES UTILITAIRES
  // ============================================================================
  
  /**
   * Vérifie si l'utilisateur a une permission spécifique
   */
  hasPermission(userPermissions: string[], permissionCode: PermissionCode): boolean {
    return userPermissions.includes(permissionCode);
  }

  /**
   * Vérifie si l'utilisateur a au moins une des permissions spécifiées
   */
  hasAnyPermission(userPermissions: string[], permissionCodes: PermissionCode[]): boolean {
    return permissionCodes.some(code => userPermissions.includes(code));
  }

  /**
   * Vérifie si l'utilisateur a toutes les permissions spécifiées
   */
  hasAllPermissions(userPermissions: string[], permissionCodes: PermissionCode[]): boolean {
    return permissionCodes.every(code => userPermissions.includes(code));
  }

  /**
   * Filtre les éléments selon les permissions de l'utilisateur
   */
  filterByPermission<T extends { permission?: PermissionCode; permissions?: PermissionCode[] }>(
    items: T[],
    userPermissions: string[]
  ): T[] {
    return items.filter(item => {
      if (item.permission) {
        return this.hasPermission(userPermissions, item.permission);
      }
      if (item.permissions) {
        return this.hasAnyPermission(userPermissions, item.permissions);
      }
      return true;
    });
  }

  /**
   * Obtient le niveau d'accès d'une permission pour un utilisateur
   */
  getPermissionAccessLevel(userPermissions: Permission[], permissionCode: PermissionCode): string {
    const permission = userPermissions.find(p => p.code === permissionCode);
    return permission?.access_level || 'NONE';
  }

  /**
   * Vérifie si l'utilisateur peut effectuer une action (WRITE ou ADMIN)
   */
  canWrite(userPermissions: Permission[], permissionCode: PermissionCode): boolean {
    const accessLevel = this.getPermissionAccessLevel(userPermissions, permissionCode);
    return ['WRITE', 'ADMIN'].includes(accessLevel);
  }

  /**
   * Vérifie si l'utilisateur peut administrer (ADMIN seulement)
   */
  canAdmin(userPermissions: Permission[], permissionCode: PermissionCode): boolean {
    const accessLevel = this.getPermissionAccessLevel(userPermissions, permissionCode);
    return accessLevel === 'ADMIN';
  }
}

// Instance singleton
const permissionService = new PermissionService();
export default permissionService;
